/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { gql, useMutation } from "@apollo/client";
import { Dropdown } from "primereact/dropdown";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { setCommonDomainData } from "./reducers/commonDomainDataReducer";
import { setStripeAccountInfo } from "./reducers/stripeInfoReducer";
import {
    resetCurrentDomain,
    resetCurrentPreset,
    setAvailableCustomers,
    setAvailableDomains,
    setAvailablePresets,
    setCurrentCustomer,
    setCurrentDomain,
    setCurrentPreset,
    setCustomerLogo,
    setCustomerRole,
    setCustomerToAdminsMappings,
    setCustomerToDomainsMapppings,
    setNavigationMode,
    setUserType,
} from "./reducers/userLoginReducer";
import { getIn } from "./utilities/utils";
import { useCommonDomainInfo } from "./hooks/useCommonDomainInfo";
import { setAdDomainData } from "./reducers/globalAdDomainDataReducer";

const PARTNER_MUTATION = gql`
    mutation callpartnerInfoMutation($id: String, $role: String) {
        partnerInfoMutation(input: { id: $id, role: $role }) {
            role
            partnerName
            customerInfo
        }
    }
`;

const DOMAIN_MUTATION = gql`
    mutation callDomainInfoMutation($id: String, $userId: String, $adminRole: Boolean) {
        domainInfoMutation(input: { id: $id, userId: $userId, adminRole: $adminRole }) {
            clientMutationId
            customerInfo
            presetInfo
            industryInfo
        }
    }
`;

const PRESET_MUTATION = gql`
    mutation callPresetInfoMutation($id: String) {
        presetInfoMutation(input: { id: $id }) {
            presetInfo
        }
    }
`;

const presetUrlExclusionList = [
    "/presetadpositions",
    "/presetslotplacementid",
    "/presetadexclusion",
    "/presetlazyload",
    "/presetidentityhub",
    "/preset-video-ad-settings",
    "/presetinfo",
    "/presetsettings"
];

const domainUrlExclusionList = [
    "/adsettings",
    "/adstxt",
    "/adpositions",
    "/slotplacementid",
    "/adexclusion",
    "/keyvalue",
    "/lazyload",
    "/adcpminfo",
    "/nativeads",
    "/customads",
    "/identityhub",
    "/supplychain",
    "/reports",
    "/domainrestoresystem",
    "/video-ad-settings",
    "/performance-preferences",
    "/exclude-urls",
    "/video-adpositions",
    "/domain-videoplacementids",
    "/video-player-positions",
    "/video-player-injections",
    "/extensions",
    '/header-bidding-settings',
    "/dashboard",
    "/",
    "/domains",
    "/sspDashboard",
    "/adapter-blocking",
    "/syndication-ad-positions",
    "/syndication-adapters"
];

const saveToLocalStorage = (k, v) => localStorage.setItem(k, v);
const getFromLocalStorage = (i) => localStorage.getItem(i);
const clearFromLocalStorage = (i) => localStorage.removeItem(i);
const saveCurrentDomain = (domainId) => saveToLocalStorage("currentDomain", domainId);
const saveCurrentCustomer = (customerId) => saveToLocalStorage("currentCustomer", customerId);
const saveCurrentPreset = (presetId) => saveToLocalStorage("currentPreset", presetId);
const getCurrentDomain = () => getFromLocalStorage("currentDomain");
const getCurrentCustomer = () => getFromLocalStorage("currentCustomer");
const getCurrentPreset = () => getFromLocalStorage("currentPreset");

const loaderIcon = <>
    <div css={css`
        display: flex;
        justify-content: space-between;
    `}>
        <div>Fetching domain...</div>
        <div>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="pi pi-spin">
                <path d="M6.99701 14C5.85441 13.999 4.72939 13.7186 3.72012 13.1832C2.71084 12.6478 1.84795 11.8737 1.20673 10.9284C0.565504 9.98305 0.165424 8.89526 0.041387 7.75989C-0.0826496 6.62453 0.073125 5.47607 0.495122 4.4147C0.917119 3.35333 1.59252 2.4113 2.46241 1.67077C3.33229 0.930247 4.37024 0.413729 5.4857 0.166275C6.60117 -0.0811796 7.76026 -0.0520535 8.86188 0.251112C9.9635 0.554278 10.9742 1.12227 11.8057 1.90555C11.915 2.01493 11.9764 2.16319 11.9764 2.31778C11.9764 2.47236 11.915 2.62062 11.8057 2.73C11.7521 2.78503 11.688 2.82877 11.6171 2.85864C11.5463 2.8885 11.4702 2.90389 11.3933 2.90389C11.3165 2.90389 11.2404 2.8885 11.1695 2.85864C11.0987 2.82877 11.0346 2.78503 10.9809 2.73C9.9998 1.81273 8.73246 1.26138 7.39226 1.16876C6.05206 1.07615 4.72086 1.44794 3.62279 2.22152C2.52471 2.99511 1.72683 4.12325 1.36345 5.41602C1.00008 6.70879 1.09342 8.08723 1.62775 9.31926C2.16209 10.5513 3.10478 11.5617 4.29713 12.1803C5.48947 12.7989 6.85865 12.988 8.17414 12.7157C9.48963 12.4435 10.6711 11.7264 11.5196 10.6854C12.3681 9.64432 12.8319 8.34282 12.8328 7C12.8328 6.84529 12.8943 6.69692 13.0038 6.58752C13.1132 6.47812 13.2616 6.41667 13.4164 6.41667C13.5712 6.41667 13.7196 6.47812 13.8291 6.58752C13.9385 6.69692 14 6.84529 14 7C14 8.85651 13.2622 10.637 11.9489 11.9497C10.6356 13.2625 8.85432 14 6.99701 14Z"
                      fill="currentColor"></path>
            </svg>
        </div>
    </div>
</>

export const DomainsDropdown = ({ hideDropdown }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    let {
        domains: domainsInfo,
        currentDomain: domain,
        currentPreset: preset,
        mode,
        presets,
        userID,
        isAdmin,
        customers,
        currentCustomer,
        customerToDomainsMappings,
        customerToAdminsMappings
    } = useSelector((state) => state.userInformation);
    const [partnerInfoMutation] = useMutation(PARTNER_MUTATION);
    const [domainInfoMutation] = useMutation(DOMAIN_MUTATION);
    const [presetInfoMutation] = useMutation(PRESET_MUTATION);
    const commonDomainInfoMutation = useCommonDomainInfo()
    const [assignedClients, setAssignedClients] = useState([]);
    const [assignedCustomers, setAssignedCustomers] = useState({});
    const [logos, setLogos] = useState({});
    const [fetchDomains, setFetchDomains] = useState(false);

    useEffect(() => {
        let savedDomain = getCurrentDomain();
        let savedCustomer = getCurrentCustomer();
        let savedPreset = getCurrentPreset();
        let isDomainMode = domainUrlExclusionList.includes(window.location.pathname);

        if (isDomainMode) {
            if (currentCustomer && domain) {
                saveCurrentCustomer(currentCustomer);
                saveCurrentDomain(domain);
            } else if (currentCustomer && !domain) {
                dispatch(setCurrentCustomer(currentCustomer));
                //return history.push("/domains");
            } else if (!domain || !currentCustomer) {
                dispatch(setCurrentDomain(savedDomain));
                dispatch(setCurrentCustomer(savedCustomer));
            }
        } else {
            if (currentCustomer && preset) {
                dispatch(setNavigationMode("preset"));
                saveCurrentCustomer(currentCustomer);
                saveCurrentPreset(preset);
                clearFromLocalStorage("currentDomain");
            } else if (currentCustomer && !preset) {
                if (presetUrlExclusionList.includes(window.location.pathname)) {
                    dispatch(setCurrentPreset(savedPreset));
                    return history.push("/presetinfo");
                }
            } else if (!currentCustomer && !preset) {
                dispatch(setNavigationMode("preset"));
                dispatch(setCurrentPreset(savedPreset));
                dispatch(setCurrentCustomer(savedCustomer));
            }
        }
    }, [domain, preset, currentCustomer, mode]);

    useEffect(() => {
        if (Array.isArray(domainsInfo) && domainsInfo.length) {
            let t = domainsInfo.find(item => item.value === domain)?.domainClientType
            if (t) {
                dispatch(setAdDomainData({ domainClientType: t }))
            }
        }
        setFetchDomains(false);
    }, [domainsInfo])

    useEffect(() => {
        if (!userID) return;
        const isAdmin = localStorage.getItem("role") === "administrator";
        let assignedClients;
        try {
            assignedClients = JSON.parse(localStorage.getItem("assignedClients"));
            assignedClients = assignedClients ? assignedClients : [];
        } catch (err) {
            assignedClients = [];
        }
        setAssignedClients(assignedClients);
        dispatch(setUserType(isAdmin));
        partnerInfoMutation({ variables: { id: userID, role: "administrator" } })
            .then((data) => {
                let customers = JSON.parse(getIn(data, "data", "partnerInfoMutation", "customerInfo"));
                let userRole = JSON.parse(data.data?.partnerInfoMutation?.role || null);

                customers.sort((c1, c2) => c1.title.localeCompare(c2.title));

                if (userRole.role !== "administrator") {
                    if (assignedClients.length >= 2) {
                        customers = customers.filter(({ id }) => assignedClients.includes(id));
                    }
                }

                setLogos(
                    customers?.reduce((acc, curr) => {
                        let { id, image } = curr;
                        acc[id] = image?.url;
                        return acc;
                    }, {})
                );
                setAssignedCustomers(
                    customers?.reduce((acc, curr) => {
                        let { type, id } = curr;
                        acc[id] = type;
                        return acc;
                    }, {})
                );
                dispatch(
                    setStripeAccountInfo(
                        customers.reduce((acc, { id, stripeCustomerId }) => {
                            acc[id] = stripeCustomerId;
                            return acc;
                        }, Object.create(null))
                    )
                );
                dispatch(
                    setAvailableCustomers(
                        customers.reduce((acc, curr) => {
                            const { title: name, id: value } = curr;
                            acc.push({ name, value });
                            return acc;
                        }, [])
                    )
                );

                dispatch(
                    setCustomerToAdminsMappings(
                        customers.reduce((acc, curr) => {
                            acc[curr.id] = new Set(curr.admins || []);
                            return acc;
                        }, {})
                    )
                );
            })
            .catch((err) => {
                console.log(err.message);
            });
    }, [userID]);

    function reduceMutationInfo(res, acc) {
        const { title: name, id: value, clientType: domainClientType } = acc;
        res.push({ name, value: `${value}`, domainClientType: domainClientType || "adgrid_wrapper" });
        return res;
    }

    useEffect(() => {
        if (domain) {
            commonDomainInfoMutation({ domainId: String(domain) })
                .then((response) => {
                    let domainData = JSON.parse(response.data?.domainCommonInfoMutation?.domainInfo || null);
                    if (domainData) {
                        dispatch(setCommonDomainData(domain, domainData));
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [domain]);

    useEffect(() => {
        if (currentCustomer) {
            let agency = assignedCustomers[currentCustomer] === "agency";
            dispatch(setCustomerRole(agency));
            dispatch(setCustomerLogo(logos[currentCustomer]));
            if (isAdmin) {
                return;
            }
            if (agency) {
                let url = new URL(window.location.href);
                let path = url.pathname.slice(1);
                let paths = {
                    marketplace: true,
                    "creative-orders": true,
                    partners: true,
                    billing: true,
                    creatives: true,
                    "custom-creatives": true,
                };
                if (path in paths) {
                    return;
                }
                history.push("/marketplace");
            }
        }
    }, [assignedCustomers, currentCustomer]);

    useEffect(() => {
        if (!customerToAdminsMappings[currentCustomer]) return;
        if (mode === "domain" && currentCustomer) {
            if (currentCustomer in customerToDomainsMappings) {
                dispatch(setAvailableDomains(customerToDomainsMappings[currentCustomer]));
                return;
            }

            domainInfoMutation({
                variables: {
                    id: currentCustomer,
                    userId: userID,
                    adminRole: localStorage.getItem("admin_role") === "true",
                },
            })
                .then((response) => {
                    let customerInfo = JSON.parse(getIn(response, "data", "domainInfoMutation", "customerInfo"));
                    const domainIds = new Set();
                    customerInfo = [...customerInfo].filter((c) => {
                        if (domainIds.has(c.id)) return false;
                        domainIds.add(c.id);
                        return true;
                    });

                    const availableDomains = customerInfo
                        .filter((domainData) => {
                            if (isAdmin || customerToAdminsMappings[currentCustomer]?.has(parseInt(userID))) {
                                return true;
                            }
                            if (Array.isArray(domainData.user_mapping_list)) {
                                try {
                                    let allowedUsers = new Set(domainData.user_mapping_list.map((data) => data?.user_name_with_domain?.ID));
                                    return allowedUsers.has(parseInt(userID));
                                } catch (err) {
                                    return false;
                                }
                            }
                            return false;
                        })
                        .reduce(reduceMutationInfo, [])
                        .sort((d1, d2) => d1.name.localeCompare(d2.name));

                    dispatch(setAvailableDomains(availableDomains));
                    dispatch(setCustomerToDomainsMapppings(currentCustomer, availableDomains));
                })
                .catch((err) => {
                    dispatch(setAvailableDomains([]));
                });
        } else if (mode === "preset" && currentCustomer) {
            presetInfoMutation({
                variables: { id: currentCustomer },
            }).then((response) => {
                const data = JSON.parse(getIn(response, "data", "presetInfoMutation", "presetInfo"));
                const availablePresets = data?.presetInfo?.reduce(reduceMutationInfo, []);
                dispatch(setAvailablePresets(availablePresets));
            });
        }
    }, [currentCustomer, mode, domain, customerToAdminsMappings]);

    const renderDropdowns = (mode, presets, domains, currentPreset, currentDomain) => {
        /* URLs that are not related to domains nor presets */
        let excludedUrls = [
            "/creatives",
            "/creative-orders",
            "/custom-creatives",
            "/marketplace",
            "/marketplace-orders",
            "/invoices",
        ];

        return excludedUrls.includes(window.location.pathname) ? null : (
            <Dropdown
                disabled={mode === "domain" ? fetchDomains : false}
                className="domains-dropdown"
                dropdownIcon="pi pi-caret-down"
                name="domains-dropdown"
                options={mode === "domain" ? domains : presets}
                value={mode === "domain" ? `${currentDomain}` : currentPreset}
                onChange={(e) => {
                    if (mode === "preset") {
                        dispatch(setCurrentPreset(e.value));
                        history.push(`/presetsettings`);
                    } else {
                        dispatch(setCurrentDomain(e.value));
                        history.push(`/adsettings`);
                    }
                }}
                optionLabel="name"
                placeholder={mode === "preset" ? "Choose a preset" : fetchDomains ? loaderIcon : "Choose a domain"}
            />
        );
    };

    return (
        <div className="domains-dropdown-container">
            {isAdmin || assignedClients.length >= 2 ? (
                <div className="admin-dropdowns">
                    <Dropdown
                        className="customers-dropdown"
                        dropdownIcon="pi pi-caret-down"
                        name="customers-dropdown"
                        options={customers}
                        value={currentCustomer || ""}
                        onChange={(e) => {
                            setFetchDomains(true);
                            dispatch(setCurrentCustomer(e.value));
                            dispatch(resetCurrentPreset());
                            dispatch(resetCurrentDomain());
                        }}
                        optionLabel="name"
                        placeholder="Choose a customer"
                    />
                    {currentCustomer && !hideDropdown && renderDropdowns(mode, presets, domainsInfo, preset, domain)}
                </div>
            ) : (
                renderDropdowns(mode, presets, domainsInfo, preset, domain)
            )}
        </div>
    );
};
